import React from "react";
import { motion } from "framer-motion";
import { Download, ShieldCheck, FilePlus2, UserPlus, UserMinus, Send, PenLine, RefreshCw, Archive, Pencil, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDateTime, shortHash } from "@/lib/contracts";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

const EVENT_ICONS = {
  contract_created: FilePlus2, contract_updated: Pencil, contract_archived: Archive,
  document_uploaded: FilePlus2, signatory_added: UserPlus, signatory_removed: UserMinus,
  signature_requested: Send, signatory_signed: PenLine, signatory_notified: Send,
  signatory_viewed: RefreshCw, signatory_declined: UserMinus, signatory_pending: RefreshCw,
  status_changed: RefreshCw,
};
const EVENT_COLORS = {
  contract_created: "text-green-500 bg-green-500/10 border-green-500/25",
  signatory_signed: "text-green-500 bg-green-500/10 border-green-500/25",
  signature_requested: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  signatory_notified: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  signatory_declined: "text-red-400 bg-red-500/10 border-red-500/25",
  contract_archived: "text-zinc-400 bg-zinc-500/10 border-zinc-500/25",
  document_uploaded: "text-blue-400 bg-blue-500/10 border-blue-500/25",
};

export const TabHistory = ({ contract }) => {
  const events = contract.events || [];

  const exportAudit = async () => {
    try {
      const r = await api.get(`/contracts/${contract.id}/audit-export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_${contract.title.slice(0, 40).replace(/\s+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(tfr("Export impossible")); }
  };

  return (
    <div className="space-y-4" data-testid="tab-history">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-border bg-surface p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">{tfr("Journal d'audit immuable")}</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {tfr("Chaque événement est chaîné par empreinte SHA-256 : toute modification a posteriori serait détectable.")}
              {" "}{events.length} {events.length > 1 ? tfr("événements enregistrés") : tfr("événement enregistré")}.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportAudit} data-testid="audit-export-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> {tfr("Exporter l'audit (CSV)")}
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">{tfr("Aucun événement.")}</div>
      ) : (
        <ol className="relative" data-testid="history-timeline">
          {events.map((e, i) => {
            const Icon = EVENT_ICONS[e.type] || RefreshCw;
            const color = EVENT_COLORS[e.type] || "text-muted-foreground bg-secondary/30 border-border";
            return (
              <motion.li key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.4) }} className="flex gap-3" data-testid={`history-event-${e.id}`}>
                <div className="flex flex-col items-center">
                  <div className={`h-8 w-8 rounded-full border flex items-center justify-center shrink-0 ${color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  {i < events.length - 1 && <div className="w-px flex-1 min-h-[16px] bg-secondary/50" />}
                </div>
                <div className="pb-5 min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm">{e.label}</p>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtDateTime(e.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                    <span>{e.actor}</span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1 font-mono"><Fingerprint className="h-3 w-3" />{shortHash(e.hash)}</span>
                    {e.meta?.sha256 && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="font-mono">doc {shortHash(e.meta.sha256)}</span>
                      </>
                    )}
                  </p>
                </div>
              </motion.li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
